import type { PropsWithChildren, ReactNode } from 'react';

export function Button({
  children,
  busy = false,
  variant = 'primary',
  ...props
}: PropsWithChildren<React.ButtonHTMLAttributes<HTMLButtonElement> & { busy?: boolean; variant?: 'primary' | 'secondary' | 'danger' | 'ghost' }>) {
  return (
    <button className={`button button--${variant}`} disabled={busy || props.disabled} {...props}>
      {busy ? <span className="spinner" aria-hidden="true" /> : null}
      <span>{children}</span>
    </button>
  );
}

export function Status({ tone = 'neutral', children }: PropsWithChildren<{ tone?: 'good' | 'warning' | 'bad' | 'neutral' }>) {
  return <span className={`status status--${tone}`}><span className="status__mark" aria-hidden="true" />{children}</span>;
}

export function Empty({ title, children }: PropsWithChildren<{ title: string }>) {
  return <div className="empty"><div className="empty__orb" aria-hidden="true">ر</div><h3>{title}</h3>{children ? <p>{children}</p> : null}</div>;
}

export function ErrorNotice({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return <div className="notice notice--error" role="alert"><span>{message}</span>{onRetry ? <Button variant="secondary" onClick={onRetry}>إعادة المحاولة</Button> : null}</div>;
}

export function Panel({ title, intro, actions, children, className = '' }: PropsWithChildren<{ title: string; intro?: string; actions?: ReactNode; className?: string }>) {
  return <section className={`panel ${className}`}><header className="panel__header"><div><h2>{title}</h2>{intro ? <p>{intro}</p> : null}</div>{actions}</header>{children}</section>;
}

export function Skeleton({ lines = 3 }: { lines?: number }) {
  return <div className="skeleton" aria-label="جارٍ التحميل">{Array.from({ length: lines }, (_, index) => <span key={index} />)}</div>;
}

export function Meter({ value, max, label }: { value: number; max: number; label: string }) {
  const percentage = max > 0 ? Math.min(100, Math.max(0, value / max * 100)) : 0;
  return <div className="meter"><div className="meter__labels"><span>{label}</span><span>{Math.round(percentage)}٪</span></div><div className="meter__track" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(percentage)}><span style={{ width: `${percentage}%` }} /></div></div>;
}
